// app_client/src/components/modals/ProductSelectModal.jsx
import React, { useState, useEffect } from 'react';
import { X, Search, Loader2, Package, Wrench, Boxes } from 'lucide-react';
import useProductStore from '../../store/useProductStore';
import Pagination from '../common/Pagination';

const ProductSelectModal = ({ isOpen, onClose, onSelect, forceType = null }) => {
  // Varsayılan olarak 'Material' (Ürün) başlasın
  const [activeType, setActiveType] = useState(forceType || 'Material');
  const [search, setSearch] = useState('');
  const { products, fetchProducts, loading, pagination } = useProductStore();

  // Modal açıldığında veya tip değiştiğinde veriyi çek
  useEffect(() => {
    if (isOpen) {
      const type = forceType || activeType;
      fetchProducts({ 
        page: 1, 
        limit: 10, 
        type, 
        search: ''
      }, { persistQuery: false });
    }
  }, [activeType, fetchProducts, forceType, isOpen]);

  const handleSearch = (e) => {
    const val = e.target.value;
    setSearch(val);
    fetchProducts({ search: val, page: 1, type: activeType }, { persistQuery: false });
  };

  const handlePageChange = (newPage) => {
    fetchProducts({ search, page: newPage, type: activeType }, { persistQuery: false });
  };

  const handleTypeChange = (type) => {
    setActiveType(type);
    setSearch(''); // Kullanıcı tıkladığı anda sıfırlıyoruz, Effect içinde değil.
    fetchProducts({ search: '', page: 1, type }, { persistQuery: false }); // Aramayı da temiz gönderiyoruz.
  };

  const handleClose = () => {
    setSearch('');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-100 flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-md">
      <div className="bg-white w-full max-w-2xl rounded-[3rem] shadow-2xl overflow-hidden border border-slate-100 animate-in zoom-in-95">
        
        {/* Header */}
        <div className="p-6 border-b border-slate-50 flex justify-between items-center bg-slate-50/50">
          <div className="flex items-center gap-3 ml-2">
            <div className={`p-2 rounded-xl ${activeType === 'Material' ? 'bg-emerald-100 text-emerald-600' : 'bg-blue-100 text-blue-600'}`}>
              {activeType === 'Material' ? <Package size={20} /> : <Wrench size={20} />}
            </div>
            <h3 className="font-black text-slate-800 uppercase tracking-tighter">
              {activeType === 'Material' ? 'Ürün Seçimi' : 'Hizmet Seçimi'}
            </h3>
          </div>
          <button onClick={handleClose} className="p-2 hover:bg-slate-200 rounded-full transition-all"><X size={20} /></button>
        </div>

        {forceType ? null : (
        <div className="px-6 pt-6">
          <div className="flex gap-2 p-1.5 bg-slate-100 rounded-4xl">
            <button
              onClick={() => handleTypeChange('Material')}
              className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-3xl font-black text-xs transition-all ${
                activeType === 'Material' 
                ? 'bg-white text-slate-900 shadow-sm' 
                : 'text-slate-400 hover:text-slate-600'
              }`}
            >
              <Boxes size={16} /> ÜRÜNLER (STOKLU)
            </button>
            <button
              onClick={() => handleTypeChange('Service')}
              className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-3xl font-black text-xs transition-all ${
                activeType === 'Service' 
                ? 'bg-white text-slate-900 shadow-sm' 
                : 'text-slate-400 hover:text-slate-600'
              }`}
            >
              <Wrench size={16} /> HİZMET / İŞÇİLİK
            </button>
          </div>
        </div>
        )}

        {/* Search */}
        <div className="p-6">
          <div className="relative">
            <Search className="absolute left-5 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
            <input 
              autoFocus
              type="text"
              placeholder={`${activeType === 'Material' ? 'Parça adı, kod veya barkod...' : 'Hizmet veya işçilik adı...'}`}
              className={`w-full pl-14 pr-6 py-4 bg-slate-50 rounded-2xl border-none font-bold text-slate-700 outline-none focus:ring-2 transition-all ${
                activeType === 'Material' ? 'ring-emerald-500/20' : 'ring-blue-500/20'
              }`}
              value={search}
              onChange={handleSearch}
            />
          </div>
        </div>

        {/* List */}
        <div className="px-4 pb-4 max-h-87.5 overflow-y-auto min-h-75">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-20 gap-3">
              <Loader2 className={`animate-spin ${activeType === 'Material' ? 'text-emerald-600' : 'text-blue-600'}`} />
              <p className="text-[10px] font-black text-slate-400 uppercase">Liste Güncelleniyor...</p>
            </div>
          ) : (
            <div className="grid gap-2">
              {products.length > 0 ? products.map((prod) => (
                <button 
                  key={prod._id}
                  onClick={() => { onSelect(prod); handleClose(); }}
                  className={`flex items-center justify-between p-4 rounded-2xl transition-all border border-transparent text-left group ${
                    activeType === 'Material' ? 'hover:bg-emerald-50 hover:border-emerald-100' : 'hover:bg-blue-50 hover:border-blue-100'
                  }`}
                >
                  <div className="flex-1">
                    <div className="font-bold text-slate-800">{prod.name}</div>
                    <div className="text-[10px] font-black text-slate-400 uppercase tracking-tighter">
                      KOD: {prod.code || '---'} {prod.unit ? `• BİRİM: ${prod.unit}` : ''}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="font-black text-slate-900">
                      {new Intl.NumberFormat('tr-TR', { style: 'currency', currency: prod.saleCurrency || 'TRY' }).format(prod.salePrice || 0)}
                    </div>
                    {activeType === 'Material' && (
                      <div className={`text-[10px] font-bold uppercase ${prod.stockQuantity <= 0 ? 'text-rose-500' : 'text-emerald-600'}`}>
                        Stok: {prod.stockQuantity || 0}
                      </div>
                    )}
                  </div>
                </button>
              )) : (
                <div className="py-20 text-center">
                  <p className="text-xs font-bold text-slate-400 uppercase">Kayıt Bulunamadı</p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Pagination */}
        <div className="border-t border-slate-50">
          <Pagination 
            currentPage={pagination.currentPage} 
            totalPages={pagination.totalPages} 
            onPageChange={handlePageChange} 
          />
        </div>
      </div>
    </div>
  );
};

export default ProductSelectModal;
